import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Loader2, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import {
  WIRED_HUD_ICONES,
  WIRED_LOJA_GRUPOS,
  WIRED_LOJA_MODOS,
  WIRED_LOJA_OPERADORES,
  WiredConexao,
  WiredLojaEfeitoExtra,
  WiredLojaRequisito,
  WiredLojaGrupo,
  WiredLojaItem,
  WiredLojaModo,
  WiredNivel,
  wiredService,
} from "../../../services/wired";

interface WiredLojaItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: WiredLojaItem | null;
  conexoes: WiredConexao[];
  onSuccess?: () => void;
}

const SEM_ICONE = "none";
const NOME_VARIAVEL_REGEX = /^[\w.-]{1,128}$/;
const INTEIRO_REGEX = /^-?\d{1,18}$/;

export function WiredLojaItemModal({ isOpen, onClose, item, conexoes, onSuccess }: WiredLojaItemModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [idConexao, setIdConexao] = useState("");
  const [grupo, setGrupo] = useState<WiredLojaGrupo>("status");
  const [modo, setModo] = useState<WiredLojaModo>("incremento");
  const [rotulo, setRotulo] = useState("");
  const [icone, setIcone] = useState(SEM_ICONE);
  const [alvo, setAlvo] = useState("");
  const [valor, setValor] = useState("1");
  const [custo, setCusto] = useState("1");
  const [teto, setTeto] = useState("");
  const [limite, setLimite] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [error, setError] = useState("");
  const [extras, setExtras] = useState<WiredLojaEfeitoExtra[]>([]);
  const [requisitos, setRequisitos] = useState<WiredLojaRequisito[]>([]);
  const [variaveis, setVariaveis] = useState<string[]>([]);
  const [carregandoVars, setCarregandoVars] = useState(false);
  const [erroVars, setErroVars] = useState("");
  const [listaAberta, setListaAberta] = useState(false);
  const [niveisConexao, setNiveisConexao] = useState<WiredNivel[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    wiredService
      .listarNiveis()
      .then((lista) => setNiveisConexao(lista))
      .catch(() => setNiveisConexao([]));
  }, [isOpen]);

  const niveisXp = niveisConexao.filter((n) => n.id_conexao === idConexao).map((n) => n.nome_variavel_xp);

  useEffect(() => {
    if (!isOpen || !idConexao) {
      setVariaveis([]);
      return;
    }
    let cancelado = false;
    setCarregandoVars(true);
    setErroVars("");
    wiredService
      .listarVariaveisConexao(idConexao)
      .then((res) => {
        if (cancelado) return;
        setVariaveis(res.usuario ?? []);
        if (!res.ok) setErroVars(res.erro ?? "Não foi possível listar as variáveis.");
      })
      .catch(() => {
        if (cancelado) return;
        setVariaveis([]);
        setErroVars("Não foi possível listar as variáveis.");
      })
      .finally(() => {
        if (!cancelado) setCarregandoVars(false);
      });
    return () => {
      cancelado = true;
    };
  }, [isOpen, idConexao]);

  const variaveisFiltradas = variaveis
    .filter((nome) => nome.toLowerCase().includes(alvo.trim().toLowerCase()))
    .slice(0, 100);

  useEffect(() => {
    if (!isOpen) return;
    setIdConexao(item?.id_conexao ?? conexoes[0]?.id ?? "");
    setGrupo(item?.grupo ?? "status");
    setModo(item?.modo ?? "incremento");
    setRotulo(item?.rotulo ?? "");
    setIcone(item?.icone ?? SEM_ICONE);
    setAlvo(item?.nome_variavel_alvo ?? "");
    setValor(item?.valor ?? "1");
    setCusto(item?.custo ?? "1");
    setTeto(item?.teto_valor ?? "");
    setExtras(item?.efeitos_extras ?? []);
    setRequisitos(item?.requisitos ?? []);
    setLimite(item?.limite_compras != null ? String(item.limite_compras) : "");
    setAtivo(item?.ativo ?? true);
    setError("");
  }, [isOpen, item, conexoes]);

  const mudar = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setError("");
  };

  const validar = (): string => {
    if (!idConexao) return "Selecione uma conexão.";
    if (!rotulo.trim() || rotulo.trim().length > 60) return "Rótulo é obrigatório (máximo 60 caracteres).";
    if (!NOME_VARIAVEL_REGEX.test(alvo.trim())) return "Variável alvo inválida (letras, números, _, - e .).";
    if (!INTEIRO_REGEX.test(valor.trim())) return "O valor deve ser um número inteiro.";
    if (modo === "incremento" && BigInt(valor.trim()) <= BigInt(0)) return "O incremento deve ser maior que 0.";
    if (!INTEIRO_REGEX.test(custo.trim()) || BigInt(custo.trim()) <= BigInt(0)) {
      return "O custo deve ser um inteiro maior que 0.";
    }
    if (modo === "incremento" && teto.trim()) {
      if (!INTEIRO_REGEX.test(teto.trim())) return "O teto deve ser um número inteiro.";
      if (BigInt(teto.trim()) < BigInt(valor.trim())) return "O teto não pode ser menor que o incremento.";
    }
    if (limite.trim()) {
      const l = Number(limite);
      if (!Number.isInteger(l) || l < 0 || l > 1_000_000) return "Limite de compras inválido (inteiro ou vazio).";
    }
    const nomes = [alvo.trim()];
    for (let i = 0; i < extras.length; i++) {
      const e = extras[i];
      const p = `Variável adicional ${i + 1}`;
      if (!e.rotulo.trim() || e.rotulo.trim().length > 60) return `${p}: rótulo é obrigatório (máximo 60 caracteres).`;
      if (!NOME_VARIAVEL_REGEX.test(e.nome_variavel.trim())) return `${p}: variável inválida.`;
      if (!INTEIRO_REGEX.test(e.valor.trim())) return `${p}: o valor deve ser um número inteiro.`;
      if (e.modo === "incremento") {
        if (BigInt(e.valor.trim()) <= BigInt(0)) return `${p}: o incremento deve ser maior que 0.`;
        if (e.teto_valor?.trim()) {
          if (!INTEIRO_REGEX.test(e.teto_valor.trim())) return `${p}: o teto deve ser um número inteiro.`;
          if (BigInt(e.teto_valor.trim()) < BigInt(e.valor.trim())) return `${p}: o teto não pode ser menor que o incremento.`;
        }
      }
      nomes.push(e.nome_variavel.trim());
    }
    if (new Set(nomes).size !== nomes.length) return "Cada variável só pode aparecer uma vez no item.";
    for (let i = 0; i < requisitos.length; i++) {
      const r = requisitos[i];
      const p = `Requisito ${i + 1}`;
      if (!r.rotulo.trim() || r.rotulo.trim().length > 60) return `${p}: rótulo é obrigatório (máximo 60 caracteres).`;
      if (!NOME_VARIAVEL_REGEX.test(r.nome_variavel.trim())) return `${p}: variável inválida.`;
      if (!INTEIRO_REGEX.test(r.valor.trim())) return `${p}: o valor deve ser um número inteiro.`;
    }
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const mensagem = validar();
    if (mensagem) {
      setError(mensagem);
      return;
    }

    const payload = {
      id_conexao: idConexao,
      grupo,
      modo,
      rotulo: rotulo.trim(),
      icone: icone === SEM_ICONE ? null : icone,
      nome_variavel_alvo: alvo.trim(),
      valor: valor.trim(),
      custo: custo.trim(),
      teto_valor: modo === "incremento" && teto.trim() ? teto.trim() : null,
      requisitos: requisitos.map((r) => ({
        tipo: niveisXp.includes(r.nome_variavel.trim()) && r.tipo === "nivel" ? ("nivel" as const) : ("variavel" as const),
        rotulo: r.rotulo.trim(),
        nome_variavel: r.nome_variavel.trim(),
        operador: r.operador,
        valor: r.valor.trim(),
      })),
      efeitos_extras: extras.map((e) => ({
        rotulo: e.rotulo.trim(),
        nome_variavel: e.nome_variavel.trim(),
        modo: e.modo,
        valor: e.valor.trim(),
        teto_valor: e.modo === "incremento" && e.teto_valor?.trim() ? e.teto_valor.trim() : null,
      })),
      limite_compras: limite.trim() ? Number(limite) : null,
      ativo,
    };

    try {
      setSubmitting(true);
      if (item) {
        await wiredService.atualizarLojaItem(item.id, payload);
        enqueueSnackbar("Item atualizado com sucesso!", { variant: "success" });
      } else {
        await wiredService.criarLojaItem(payload);
        enqueueSnackbar("Item criado com sucesso!", { variant: "success" });
      }
      onClose();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar item", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const ajudaModo = WIRED_LOJA_MODOS.find((m) => m.value === modo)?.ajuda;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {item ? "Editar item da loja" : "Novo item da loja"}
            </CardTitle>
            <CardDescription>Item comprado com pontos de atributo</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0" disabled={submitting}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Conexão</Label>
              <Select value={idConexao} onValueChange={setIdConexao} disabled={submitting}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conexão" />
                </SelectTrigger>
                <SelectContent>
                  {conexoes.map((conexao) => (
                    <SelectItem key={conexao.id} value={conexao.id}>
                      {conexao.nome} (quarto {conexao.room_id}){!conexao.ativo ? " - inativa" : ""}
                      {!conexao.possui_chave_escrita ? " - sem chave de escrita" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="loja_rotulo">Rótulo</Label>
                <Input
                  id="loja_rotulo"
                  value={rotulo}
                  onChange={mudar(setRotulo)}
                  placeholder="Ex: Estamina máxima"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label>Grupo</Label>
                <Select value={grupo} onValueChange={(v) => setGrupo(v as WiredLojaGrupo)} disabled={submitting}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WIRED_LOJA_GRUPOS.map((g) => (
                      <SelectItem key={g.value} value={g.value}>
                        {g.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-border p-3">
              <div>
                <Label>O que a compra altera</Label>
                <p className="text-xs text-muted-foreground">{ajudaModo}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Modo</Label>
                  <Select value={modo} onValueChange={(v) => setModo(v as WiredLojaModo)} disabled={submitting}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WIRED_LOJA_MODOS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="loja_alvo">Variável alvo</Label>
                  <div className="relative">
                    <Input
                      id="loja_alvo"
                      value={alvo}
                      onChange={(e) => {
                        setAlvo(e.target.value);
                        setError("");
                        setListaAberta(true);
                      }}
                      onFocus={() => setListaAberta(true)}
                      onBlur={() => setTimeout(() => setListaAberta(false), 150)}
                      placeholder={carregandoVars ? "Carregando variáveis..." : "Buscar variável"}
                      className="font-mono"
                      autoComplete="off"
                      disabled={submitting}
                    />
                    {listaAberta && variaveisFiltradas.length > 0 && (
                      <ul className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md">
                        {variaveisFiltradas.map((nome) => (
                          <li
                            key={nome}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setAlvo(nome);
                              setListaAberta(false);
                              setError("");
                            }}
                            className={`cursor-pointer px-3 py-1.5 font-mono text-sm hover:bg-accent ${
                              nome === alvo ? "bg-accent" : ""
                            }`}
                          >
                            {nome}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {erroVars ? (
                    <p className="text-xs text-muted-foreground">{erroVars} Digite o nome manualmente.</p>
                  ) : alvo.trim() && variaveis.length > 0 && !variaveis.includes(alvo.trim()) ? (
                    <p className="text-xs text-amber-600">Esta variável não existe na conexão selecionada.</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="loja_valor">{modo === "incremento" ? "Incremento" : "Valor"}</Label>
                  <Input
                    id="loja_valor"
                    value={valor}
                    onChange={mudar(setValor)}
                    inputMode="numeric"
                    className="font-mono"
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="loja_teto">Teto (opcional)</Label>
                  <Input
                    id="loja_teto"
                    value={teto}
                    onChange={mudar(setTeto)}
                    inputMode="numeric"
                    placeholder="Sem teto"
                    className="font-mono"
                    disabled={submitting || modo !== "incremento"}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <Label>Variáveis adicionais</Label>
                  <p className="text-xs text-muted-foreground">
                    Alteradas na mesma compra (ex.: Arma X também define Dano e Munição por carregador).
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={submitting || extras.length >= 10}
                  onClick={() =>
                    setExtras((atual) => [
                      ...atual,
                      { rotulo: "", nome_variavel: "", modo: "valor_fixo", valor: "1", teto_valor: null },
                    ])
                  }
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              <datalist id="loja_variaveis_lista">
                {variaveis.map((nome) => (
                  <option key={nome} value={nome} />
                ))}
              </datalist>
              {extras.map((e, i) => {
                const alterar = (parcial: Partial<WiredLojaEfeitoExtra>) => {
                  setExtras((atual) => atual.map((x, j) => (j === i ? { ...x, ...parcial } : x)));
                  setError("");
                };
                return (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Rótulo</Label>
                      <Input
                        value={e.rotulo}
                        onChange={(ev) => alterar({ rotulo: ev.target.value })}
                        placeholder="Ex: Dano"
                        disabled={submitting}
                      />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Variável</Label>
                      <Input
                        list="loja_variaveis_lista"
                        value={e.nome_variavel}
                        onChange={(ev) => alterar({ nome_variavel: ev.target.value })}
                        className="font-mono"
                        autoComplete="off"
                        disabled={submitting}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Modo</Label>
                      <Select
                        value={e.modo}
                        onValueChange={(v) => alterar({ modo: v as WiredLojaModo })}
                        disabled={submitting}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WIRED_LOJA_MODOS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{e.modo === "incremento" ? "Incremento" : "Valor"}</Label>
                      <Input
                        value={e.valor}
                        onChange={(ev) => alterar({ valor: ev.target.value })}
                        inputMode="numeric"
                        className="font-mono"
                        disabled={submitting}
                      />
                    </div>
                    {e.modo === "incremento" && (
                      <div className="space-y-1 sm:col-span-2">
                        <Label className="text-xs">Teto (opcional)</Label>
                        <Input
                          value={e.teto_valor ?? ""}
                          onChange={(ev) => alterar({ teto_valor: ev.target.value })}
                          inputMode="numeric"
                          placeholder="Sem teto"
                          className="font-mono"
                          disabled={submitting}
                        />
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-600"
                      disabled={submitting}
                      onClick={() => setExtras((atual) => atual.filter((_, j) => j !== i))}
                      aria-label="Remover variável adicional"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <Label>Requisitos para comprar</Label>
                  <p className="text-xs text-muted-foreground">
                    Condições sobre variáveis do jogador (ex.: Atirador ≥ 3). Variável ausente conta como 0.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={submitting || requisitos.length >= 10}
                  onClick={() =>
                    setRequisitos((atual) => [
                      ...atual,
                      { rotulo: "", nome_variavel: "", operador: ">=", valor: "1" },
                    ])
                  }
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              {requisitos.map((r, i) => {
                const alterar = (parcial: Partial<WiredLojaRequisito>) => {
                  setRequisitos((atual) => atual.map((x, j) => (j === i ? { ...x, ...parcial } : x)));
                  setError("");
                };
                return (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Rótulo</Label>
                      <Input
                        value={r.rotulo}
                        onChange={(ev) => alterar({ rotulo: ev.target.value })}
                        placeholder="Ex: Atirador"
                        disabled={submitting}
                      />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Variável</Label>
                      <Input
                        list="loja_variaveis_lista"
                        value={r.nome_variavel}
                        onChange={(ev) => alterar({ nome_variavel: ev.target.value })}
                        className="font-mono"
                        autoComplete="off"
                        disabled={submitting}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Condição</Label>
                      <Select
                        value={r.operador}
                        onValueChange={(v) => alterar({ operador: v as WiredLojaRequisito["operador"] })}
                        disabled={submitting}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WIRED_LOJA_OPERADORES.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Valor</Label>
                      <Input
                        value={r.valor}
                        onChange={(ev) => alterar({ valor: ev.target.value })}
                        inputMode="numeric"
                        className="font-mono"
                        disabled={submitting}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-600"
                      disabled={submitting}
                      onClick={() => setRequisitos((atual) => atual.filter((_, j) => j !== i))}
                      aria-label="Remover requisito"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {niveisXp.includes(r.nome_variavel.trim()) && (
                      <div className="col-span-full flex items-center gap-2 text-xs text-muted-foreground">
                        <span>Esta variável tem sistema de nível. Comparar por:</span>
                        <Select
                          value={r.tipo === "nivel" ? "nivel" : "variavel"}
                          onValueChange={(v) => alterar({ tipo: v as "nivel" | "variavel" })}
                          disabled={submitting}
                        >
                          <SelectTrigger className="h-8 w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nivel">Nível</SelectItem>
                            <SelectItem value="variavel">XP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="loja_custo">Custo (pontos de atributo)</Label>
                <Input
                  id="loja_custo"
                  value={custo}
                  onChange={mudar(setCusto)}
                  inputMode="numeric"
                  className="font-mono"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="loja_limite">Limite de compras por militar</Label>
                <Input
                  id="loja_limite"
                  value={limite}
                  onChange={mudar(setLimite)}
                  inputMode="numeric"
                  placeholder="Ilimitado"
                  className="font-mono"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ícone</Label>
                <Select value={icone} onValueChange={setIcone} disabled={submitting}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sem ícone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SEM_ICONE}>Sem ícone</SelectItem>
                    {WIRED_HUD_ICONES.map((nome) => (
                      <SelectItem key={nome} value={nome}>
                        {nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3 self-end">
                <Label htmlFor="loja_ativo">Ativo</Label>
                <Switch id="loja_ativo" checked={ativo} onCheckedChange={setAtivo} disabled={submitting} />
              </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : item ? (
                  "Atualizar"
                ) : (
                  "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
